/**
 * P17-10 · Buy now, pay later — the one place that decides whether a BNPL
 * promise may appear on a page.
 *
 * Three facts have to agree before a shopper is shown "Pay in 3", and P15-03
 * is the reason they are decided here rather than at each surface: the PayPal
 * badge sat in the footer of a shop whose PayPal lane was closed for a whole
 * phase, because the badge list and the tender list were two different
 * decisions made in two different files. A badge is a promise the checkout has
 * to keep, so the footer badge, the PDP badge, the PDP price message and the
 * cart message all ask this module and get the same answer.
 *
 * The three facts:
 *
 *  1. the `bnpl` flag is on for this brand;
 *  2. the operator has put a BNPL badge in `paymentBadges` — which one it is
 *     also chooses which method the message advertises;
 *  3. the amount is quoted in a currency Stripe's messaging element supports.
 *
 * The fact this module CANNOT check is whether the method is switched on in
 * the brand's Stripe dashboard: that is owner-held and there is no API on our
 * side of the account that reveals it. The flag's preflight warns about it in
 * words; nothing here pretends to know.
 */
import type { PaymentBadge } from "~/server/api/schemas/brand-settings";

/**
 * The badge keys that name a BNPL method, and the Stripe payment-method type
 * each one maps to. `clearpay` is the UK name for the method Stripe calls
 * `afterpay_clearpay` — one method, two brand names, and the badge says the
 * one a British shopper recognises.
 */
export const BNPL_STRIPE_TYPE = {
  klarna: "klarna",
  clearpay: "afterpay_clearpay",
} as const satisfies Partial<Record<PaymentBadge, string>>;

export type BnplBadge = keyof typeof BNPL_STRIPE_TYPE;
export type BnplStripeType = (typeof BNPL_STRIPE_TYPE)[BnplBadge];

const BNPL_BADGES = Object.keys(BNPL_STRIPE_TYPE) as BnplBadge[];

export function isBnplBadge(badge: PaymentBadge): badge is BnplBadge {
  return (BNPL_BADGES as PaymentBadge[]).includes(badge);
}

/**
 * The currencies Stripe's payment-method-messaging element accepts, verbatim
 * from `StripePaymentMethodMessagingElementOptions`. Passing anything else
 * throws in the browser, so a shop switched to a currency outside this set
 * shows no message rather than a broken element — which is also the correct
 * behaviour, since the method would not be offered at that checkout either.
 */
const MESSAGING_CURRENCIES = [
  "AUD",
  "CAD",
  "CHF",
  "CZK",
  "DKK",
  "EUR",
  "GBP",
  "NOK",
  "NZD",
  "PLN",
  "SEK",
  "USD",
] as const;

export type BnplMessagingCurrency = (typeof MESSAGING_CURRENCIES)[number];

export function isBnplMessagingCurrency(
  currency: string,
): currency is BnplMessagingCurrency {
  return (MESSAGING_CURRENCIES as readonly string[]).includes(
    currency.toUpperCase(),
  );
}

/**
 * The badges this shop may actually show.
 *
 * A BNPL badge with the flag off is exactly the P15-03 failure — a logo in the
 * footer for a method the checkout will not offer — so it is dropped rather
 * than rendered. Every other badge is unaffected: this function is not a
 * general badge filter, it is the BNPL half of one.
 */
export function visiblePaymentBadges(
  badges: PaymentBadge[],
  bnplEnabled: boolean,
): PaymentBadge[] {
  if (bnplEnabled) return badges;
  return badges.filter((badge) => !isBnplBadge(badge));
}

/** The BNPL methods an operator has badged, in the order they badged them. */
export function bnplMethodsFromBadges(badges: PaymentBadge[]): BnplStripeType[] {
  const out: BnplStripeType[] = [];
  for (const badge of badges) {
    if (!isBnplBadge(badge)) continue;
    const type = BNPL_STRIPE_TYPE[badge];
    if (!out.includes(type)) out.push(type);
  }
  return out;
}

/** What a surface needs to mount the messaging element. */
export interface BnplMessagingConfig {
  publishableKey: string;
  /** Stripe Connect account the brand charges on, when it uses one. */
  stripeAccount: string | null;
  /** Non-empty: a config with no method is `null`, not an empty message. */
  methods: BnplStripeType[];
}

/**
 * Whether THIS amount, in THIS currency, may carry a message — the last gate,
 * asked per surface because the PDP price and the cart total are different
 * amounts in the same shop.
 *
 * A zero or negative amount is refused because "Pay in 3 interest-free
 * payments of £0.00" is the kind of copy that ends up in a screenshot. Stripe
 * applies the real per-method floor and ceiling itself and renders nothing
 * outside them; we do not restate those numbers here, because they differ per
 * method and per country and a stale copy of them would be a promise we do not
 * keep.
 */
export function canShowBnplMessage(
  config: BnplMessagingConfig | null | undefined,
  amountMinor: number | null | undefined,
  currency: string,
): config is BnplMessagingConfig {
  if (!config || config.methods.length === 0) return false;
  if (typeof amountMinor !== "number" || amountMinor <= 0) return false;
  return isBnplMessagingCurrency(currency);
}
